import { useTimelineContext } from '@/pages/PatientTimelinePage/hooks/useTimelineContext';
import { buildPacIntroCallPreview, isPacIntroCallAnswers } from '@bariendo/lib/pac-intro-call-note';
import { buildCiIntroCallPreview, hasAnyAnswer, isPnConsultationAnswers } from '@bariendo/lib/patient-access-note';
import { answersThatApply } from '@bariendo/lib/beat-applicability';
import { buildPnConsultationLine, hasPnConsultationAnswer } from '@bariendo/lib/pn-consultation-note';
import { buildPnConsultationScript } from '@bariendo/lib/pn-consultation-script';
import { isPnCardV2Note } from '@bariendo/lib/pn-consultation-v2-script';
import { FEATURE_FLAGS } from '@bariendo/lib/constants';
import { Menu } from '@mantine/core';
import { getReferenceString } from '@medplum/core';
import { Communication } from '@medplum/fhirtypes';
import { useFeatureFlagEnabled } from '@/hooks/useFeatureFlag';
import { IconEye, IconEyeOff } from '@tabler/icons-react';
import { useState } from 'react';
import { PacIntroCallTimelineGroup } from '../../types';
import { CARD_MENU_ICON_COLOR } from '../CardMenu';
import { PacIntroCallCard } from './PacIntroCallCard';
import { PAC_INTRO_CALL_CARD_TITLE, PAC_INTRO_CALL_LABELS } from './pacIntroCallRow';
import { CallCardLabels, PatientAccessCallTimelineItem } from './PatientAccessCallTimelineItem';
import { PnConsultationCard } from './PnConsultationCard';
import { PnConsultationGlanceRow } from './PnConsultationGlanceRow';
import { buildPnConsultationGlance } from '@bariendo/lib/pn-consultation-v2-script';
import { PN_CONSULTATION_CARD_TITLE, PN_CONSULTATION_LABELS } from './pnConsultationRow';
import { ScheduledIntroCallCard } from './ScheduledIntroCallCard';

/** The legacy scheduled card, kept for the notes it already wrote. */
const SCHEDULED_INTRO_CALL_LABELS: CallCardLabels = {
  start: 'Start call notes',
  show: 'Show call notes',
  hide: 'Hide call notes',
};

interface PacIntroCallTimelineItemProps {
  group: PacIntroCallTimelineGroup;
  highlightRef?: string | null;
  copyDirectLink: (itemReference: string) => Promise<void>;
}

/**
 * The script the collapsed PN line reads applicability from. The pregnancy
 * question is the only part that varies, and the line reads nothing from it.
 */
const PN_LINE_SCRIPT = buildPnConsultationScript({ askPregnancy: false, keyFacts: false });

/**
 * The intro-call note on the timeline — three scripts on one note category, told
 * apart by `visitType`.
 *
 * `pn-consultation` is the PN consultation (Intake 1.1), `inbound` the PAC intro
 * call (BAR-2612), and `zoom` the scheduled call both of those replaced. Each
 * brings its own title, labels, preview and card; the row announces which so the
 * team can see at a glance what kind of call this was.
 */
export function PacIntroCallTimelineItem({ group, highlightRef, copyDirectLink }: PacIntroCallTimelineItemProps) {
  const item = group.items[0];
  const { patientContext } = useTimelineContext();
  const isPnConsultation = isPnConsultationAnswers(item.answers);
  const isPacIntroCall = isPacIntroCallAnswers(item.answers);
  // The PN card v2.0 (BAR-2994). Off, the shipped card exactly as it was,
  // except for a note already worked on v2.0, which stays on v2.0.
  const flagOn = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_PN_CARD_V2);
  const pnCardV2 = isPnConsultation && (flagOn || isPnCardV2Note(item.answers));
  const [hideScript, setHideScript] = useState(false);

  const cardProps = {
    note: item.resource as unknown as Communication,
    answers: item.answers,
    patient: patientContext?.patient,
    readOnly: !item.isDraft,
  };

  return (
    <PatientAccessCallTimelineItem
      title={
        isPnConsultation
          ? PN_CONSULTATION_CARD_TITLE
          : isPacIntroCall
            ? PAC_INTRO_CALL_CARD_TITLE
            : 'Scheduled intro call'
      }
      reference={getReferenceString(item.reference)!}
      timestamp={item.timestamp}
      sender={item.sender}
      isDraft={item.isDraft}
      // The PN row reads "worked" the way its line reads status: what the card
      // prefilled or computed on its own doesn't count.
      worked={isPnConsultation ? hasPnConsultationAnswer(item.answers) : hasAnyAnswer(item.answers)}
      preview={
        isPnConsultation
          ? {
              // Applied against the loaded Patient (the section's age rule reads
              // it); until it loads, the saved answers as they are, rather than
              // a line that drops the insurance parts and then brings them back.
              line: buildPnConsultationLine(
                patientContext?.patient
                  ? answersThatApply(PN_LINE_SCRIPT, item.answers, patientContext.patient)
                  : item.answers
              ),
            }
          : isPacIntroCall
            ? buildPacIntroCallPreview(item.answers)
            : buildCiIntroCallPreview(item.answers)
      }
      labels={
        isPnConsultation ? PN_CONSULTATION_LABELS : isPacIntroCall ? PAC_INTRO_CALL_LABELS : SCHEDULED_INTRO_CALL_LABELS
      }
      highlightRef={highlightRef}
      copyDirectLink={copyDirectLink}
      // v2.0 draws the saved card on the timeline card canon: a glance row, no status line.
      glance={pnCardV2 ? <PnConsultationGlanceRow glance={buildPnConsultationGlance(item.answers)} /> : undefined}
      menuItems={
        pnCardV2 ? (
          <Menu.Item
            leftSection={
              hideScript ? (
                <IconEye size={18} color={CARD_MENU_ICON_COLOR} />
              ) : (
                <IconEyeOff size={18} color={CARD_MENU_ICON_COLOR} />
              )
            }
            onClick={() => setHideScript((hidden) => !hidden)}
            data-testid="pn-toggle-script"
          >
            {hideScript ? 'Show script' : 'Hide script'}
          </Menu.Item>
        ) : undefined
      }
      renderCard={(onSaved, charted) =>
        isPnConsultation ? (
          pnCardV2 ? (
            // v2.0: a saved card stays editable when reopened (BAR-2996).
            <PnConsultationCard
              {...cardProps}
              readOnly={false}
              charted={charted ?? !item.isDraft}
              onSaved={onSaved}
              v2
              hideScript={hideScript}
            />
          ) : (
            <PnConsultationCard {...cardProps} onSaved={onSaved} />
          )
        ) : isPacIntroCall ? (
          <PacIntroCallCard {...cardProps} onSaved={onSaved} />
        ) : (
          <ScheduledIntroCallCard {...cardProps} onSaved={onSaved} />
        )
      }
    />
  );
}
